import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useEffect, useRef, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "./arbiter-components/modal/redo-modal";
import { TeamContext } from "./context/team-context";
import { Flex } from "./flex";
import { Text } from "./text";

interface LegalAgreementModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  imageUrl: string;
  requireScroll?: boolean;
  onClose: () => void;
  onAccepted: (accepted: boolean) => void;
}

export const LegalAgreementModal = memo(function LegalAgreementModal({
  isOpen,
  title,
  description,
  imageUrl,
  requireScroll,
  onClose,
  onAccepted,
}: LegalAgreementModalProps) {
  const team = useRequiredContext(TeamContext);
  const [accepted, setAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  return (
    <RedoModal isOpen={isOpen} onModalCloseRequested={onClose} title={title}>
      <Flex dir="column" gap="md" justify="flex-end">
        <LegalAgreementContent
          accepted={accepted}
          description={description}
          hasScrolledToBottom={hasScrolledToBottom}
          imageUrl={imageUrl}
          requireScroll={requireScroll}
          setAccepted={setAccepted}
          setHasScrolledToBottom={setHasScrolledToBottom}
        />
        <Flex dir="row" gap="md" justify="flex-end">
          <RedoButton
            disabled={!team}
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            disabled={!accepted}
            hierarchy="primary"
            onClick={() => onAccepted(accepted)}
            size="md"
            text="Accept Agreement"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});

export const LegalAgreementContent = memo(function LegalAgreementContent({
  description,
  imageUrl,
  requireScroll,
  accepted,
  setAccepted,
  setHasScrolledToBottom,
  hasScrolledToBottom,
}: {
  description: string;
  imageUrl: string;
  requireScroll?: boolean;
  accepted: boolean;
  setAccepted: (accepted: boolean) => void;
  setHasScrolledToBottom: (hasScrolledToBottom: boolean) => void;
  hasScrolledToBottom: boolean;
}) {
  const handleFinishedScrolling = useHandler(() => {
    setHasScrolledToBottom(true);
  });

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="lg"
      p="sm"
    >
      <Flex dir="column" gap="lg" p="sm">
        <ImageScrollViewer
          alt={description}
          onFinishedScrolling={handleFinishedScrolling}
          src={imageUrl}
        />
        {requireScroll && (
          <Text fontSize="xs" textColor="error">
            Please scroll to the bottom of the page to accept the agreement.
          </Text>
        )}
        <RedoCheckbox
          description={description}
          disabled={requireScroll && !hasScrolledToBottom}
          setValue={(value: boolean) => setAccepted(value)}
          size="sm"
          value={accepted}
        />
      </Flex>
    </Flex>
  );
});

const ImageScrollViewer = memo(function ImageScrollViewer({
  src,
  alt,
  onFinishedScrolling,
}: {
  src: string;
  alt: string;
  onFinishedScrolling: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const onScroll = () => {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      if (atBottom) {
        onFinishedScrolling();
      }
    };

    el.addEventListener("scroll", onScroll);

    return () => el.removeEventListener("scroll", onScroll);
  }, [onFinishedScrolling]);

  return (
    <div>
      <div ref={containerRef} style={{ height: "400px", overflowY: "scroll" }}>
        <img alt={alt} src={src} style={{ width: "100%", display: "block" }} />
      </div>
    </div>
  );
});
